/*
 * Demo Profiles — answer banks used by the admin-only "Demo Mode" auto-complete
 * controls. These are NEVER imported into Customer Portal code paths.
 *
 * Edit profile content here to refresh demo behavior without touching component
 * logic. All four files live in the same place by design (single source of truth).
 *
 * Visibility: components that import this file ALSO guard rendering on
 * `useIsDemoEligible()` so non-admin roles never see the control bar or hear
 * about it.
 */

/* ─── Generic profile shape ───────────────────────────────────────────
 *   answer_value : the value sent in `response_value` for scored / Y-N items.
 *                  - For 1-5 scales, pass the number as a string ("4")
 *                  - For Yes/No, pass "yes" or "no"
 *                  - For multi-choice with custom values, the bar tries to match
 *                    by value first; falls back to picking by `.label` substring;
 *                    falls back to the first option (with console warning).
 *   long_text    : the canned narrative dropped into long_text questions.
 *   short_text   : the canned narrative dropped into short_text questions.
 * ─────────────────────────────────────────────────────────────────── */

const LOW = {
  label: "Low AI Proficiency",
  answer_value: "2",      // 1-5 scale → leans low
  yes_no: "no",
  long_text: "We do not currently use any AI tools in our operations. Data is spread across spreadsheets and email with no central system. Most of our processes are manual and handled by staff. Leadership is aware of AI but has not explored it formally. No budget has been set aside for AI initiatives at this time. Staff spend significant time on repetitive administrative tasks.",
  short_text: "Approximately 0-10 / year.",
};

const MEDIUM = {
  label: "Medium AI Proficiency",
  answer_value: "3",
  yes_no: "yes",
  long_text: "We use AI tools informally — mostly ChatGPT for drafting and research. We have a CRM and some spreadsheets but data is not fully centralized. A few workflows are automated but most still require manual handling. Leadership sees value in AI but we have not built a formal strategy. We are open to investing but have not committed a formal AI budget. Our biggest challenges are response time and getting clean reporting.",
  short_text: "Approximately 10-25 / year.",
};

const HIGH = {
  label: "High AI Proficiency",
  answer_value: "4",
  yes_no: "yes",
  long_text: "We actively use AI across several functions including drafting, analysis, and automation. Our data lives in structured systems and is largely clean and accessible. We have automated significant portions of our admin and reporting workflows. Leadership treats AI as a strategic priority with dedicated resources. We have a formal AI budget and are actively evaluating or implementing solutions. We are looking to move beyond basic AI use into deeper workflow integration.",
  short_text: "Approximately 25+ / year, sometimes more.",
};

export const PRELIMINARY_PROFILES = {
  low: LOW,
  medium: MEDIUM,
  high: HIGH,
};

// In-depth uses the same base profile shapes — but Q&A texts are richer per the
// spec. The component currently maps long_text and yes/no/scored using the same
// per-profile values; specialized per-question text can be added later if a demo
// needs it.
export const INDEPTH_PROFILES = {
  low: {
    ...LOW,
    long_text: "Most of our processes are not formally documented and rely on tribal knowledge. Data quality is a recurring issue — we spend significant time cleaning and reconciling. Our systems do not talk to each other; staff re-enter data across platforms. Reporting is produced on request and takes significant manual effort each time. Some staff may be resistant to change — we would need a clear change management plan. The biggest drag on our team is repetitive data entry and back-and-forth communication. We have not attempted any AI or automation initiatives previously. We have standard data privacy requirements but no complex compliance obligations.",
  },
  medium: {
    ...MEDIUM,
    long_text: "Some departments have documented workflows but it is inconsistent across the organization. Data quality is reasonable in our core systems but gaps exist in reporting and history. A few systems are connected but most data movement still requires manual effort. We produce monthly reports but the process takes more time than it should. Most staff are open to new tools if the value is clear and training is provided. Work slows at approval stages and when information is spread across too many places. A few team members have used AI tools informally but nothing coordinated or measured. We have data sensitivity requirements around client and employee information.",
  },
  high: {
    ...HIGH,
    long_text: "Core workflows are documented and we maintain SOPs that are updated regularly. Our data is largely clean, structured, and accessible from our primary systems. Our core systems are integrated and data flows automatically between most platforms. Leadership has access to dashboards and we produce structured weekly reporting. Staff are generally comfortable with AI tools and receptive to expanding their use. We have good foundations but need to move from ad hoc AI use to systematic workflow integration. We have run formal AI pilots and have at least one workflow in production use. We have formal AI governance policies including data handling, access controls, and review protocols.",
  },
};

/* ───────────────────────────────────────────────────────────────────
 * Auto-profile picker — Feature 1 enhancement.
 * Given a prospect (with industry + employee_count or similar), suggest the
 * best Low / Medium / High profile. The consultant can still override with the
 * dropdown.
 * ─────────────────────────────────────────────────────────────────── */
export function suggestProfile({ industry, employee_count, ai_maturity_hint } = {}) {
  if (ai_maturity_hint === "high") return "high";
  if (ai_maturity_hint === "low") return "low";

  // CED / community / nonprofit prospects skew low-to-medium
  if (industry && /community_economic_development|nonprofit|community/i.test(industry)) {
    return employee_count >= 50 ? "medium" : "low";
  }
  // Tech / SaaS / professional services skew medium-to-high
  if (industry && /(tech|saas|professional_services|legal|accounting|consulting)/i.test(industry)) {
    return employee_count >= 100 ? "high" : "medium";
  }
  // Default
  if ((employee_count || 0) >= 200) return "high";
  if ((employee_count || 0) >= 25) return "medium";
  return "low";
}


/* ───────────────────────────────────────────────────────────────────
 *  OPPORTUNITY_SCORE_BEST_CASE — Feature 3 data per industry.
 * ─────────────────────────────────────────────────────────────────── */
export const OPPORTUNITY_SCORE_BEST_CASE = {
  charter_school: {
    industry_label: "K-12 School",
    overall_score: 74,
    ai_maturity_score: 38,
    workflow_friction_score: 81,
    hours_recovered_per_week: 22,
    top_workflows: [
      { rank: 1, name: "Admissions Triage & Follow-Up",   impact: "High",   estimated_annual_roi: "$38,000",
        description: "Auto-classify and route 400+ annual applications; draft parent responses" },
      { rank: 2, name: "Parent Communication Portal",      impact: "High",   estimated_annual_roi: "$24,000",
        description: "AI-drafted updates, policy Q&A, and meeting summaries for families" },
      { rank: 3, name: "Staff Policy Knowledge Assistant", impact: "Medium", estimated_annual_roi: "$14,000",
        description: "Instant answers from HR docs, reducing repeat questions to leadership" },
    ],
    kpis: {
      admin_hours_saved: "22 hrs/wk",
      response_time_reduction: "-68%",
      headcount_avoided: "1.5 FTE (~$89K/yr)",
      year_1_roi: "$76,000",
    },
    recommended_next_step: "Propose a 90-day AI Workflow Implementation — starting with Admissions Triage and Parent Comms",
  },
  community_nonprofit: {
    industry_label: "Community Nonprofit",
    overall_score: 68,
    ai_maturity_score: 31,
    workflow_friction_score: 77,
    hours_recovered_per_week: 18,
    top_workflows: [
      { rank: 1, name: "Grant Renewal Drafting Assistant", impact: "High",   estimated_annual_roi: "$45,000",
        description: "AI-assisted narrative generation from prior grant data and impact reports" },
      { rank: 2, name: "Donor Communication Automation",   impact: "High",   estimated_annual_roi: "$22,000",
        description: "Personalized outreach, acknowledgment letters, and follow-up sequencing" },
      { rank: 3, name: "Program Impact Reporting",         impact: "Medium", estimated_annual_roi: "$16,000",
        description: "Turn spreadsheet data into board-ready narrative summaries automatically" },
    ],
    kpis: {
      grant_writing_time_reduction: "-55%",
      donor_response_rate_lift: "+31%",
      headcount_avoided: "1.0 FTE (~$62K/yr)",
      year_1_roi: "$83,000",
    },
    recommended_next_step: "Propose a 60-day Grant Automation Pilot — immediate ROI ahead of Q4 renewal season",
  },
  community_economic_development: {
    industry_label: "Community & Economic Development",
    overall_score: 72,
    ai_maturity_score: 35,
    workflow_friction_score: 80,
    hours_recovered_per_week: 21,
    top_workflows: [
      { rank: 1, name: "Grant & RFP Command Center",            impact: "High",   estimated_annual_roi: "$52,000",
        description: "Single command center for finding, drafting, and tracking grants, RFPs, and renewals" },
      { rank: 2, name: "Impact Reporting & Funder Proof",       impact: "High",   estimated_annual_roi: "$36,000",
        description: "Board, donor, and funder reports assembled without manual cleanup" },
      { rank: 3, name: "Executive Visibility Dashboard",        impact: "Medium", estimated_annual_roi: "$22,000",
        description: "Live executive view of deadlines, owners, status, and red flags across programs" },
    ],
    kpis: {
      proposal_drafting_time_reduction: "-48%",
      board_report_prep_reduction: "-62%",
      grants_pursued_lift: "+40%",
      year_1_roi: "$110,000",
    },
    recommended_next_step: "Propose a 90-day Funding & Visibility Pilot — Grant & RFP Command Center + Impact Reporting in lockstep",
  },
  netsuite_customer: {
    industry_label: "NetSuite Customer",
    overall_score: 82,
    ai_maturity_score: 54,
    workflow_friction_score: 88,
    hours_recovered_per_week: 31,
    top_workflows: [
      { rank: 1, name: "Financial Variance Commentary",   impact: "High", estimated_annual_roi: "$52,000",
        description: "Auto-generate management narrative from NetSuite period-over-period data" },
      { rank: 2, name: "AR Collections Intelligence",     impact: "High", estimated_annual_roi: "$41,000",
        description: "Prioritize overdue accounts, draft collection emails, surface dispute patterns" },
      { rank: 3, name: "Exception & Anomaly Detection",   impact: "High", estimated_annual_roi: "$29,000",
        description: "Surface threshold breaches and unusual transactions before month-end close" },
    ],
    kpis: {
      close_cycle_reduction: "-3 days",
      ar_recovery_rate_lift: "+22%",
      finance_hours_saved: "31 hrs/wk",
      year_1_roi: "$122,000",
    },
    recommended_next_step: "Propose a NetSuite AI Layer engagement — CFO dashboard and AR intelligence as Phase 1",
  },
  accounting_firm: {
    industry_label: "Accounting Firm",
    overall_score: 71,
    ai_maturity_score: 44,
    workflow_friction_score: 79,
    hours_recovered_per_week: 26,
    top_workflows: [
      { rank: 1, name: "Client Deliverable Drafting",   impact: "High",   estimated_annual_roi: "$61,000",
        description: "AI-generated tax memo, management letter, and advisory report first drafts" },
      { rank: 2, name: "Engagement Status Reporting",   impact: "Medium", estimated_annual_roi: "$28,000",
        description: "Auto-compile client progress summaries from workpaper notes and time entries" },
      { rank: 3, name: "Internal Knowledge Assistant",  impact: "Medium", estimated_annual_roi: "$19,000",
        description: "Tax code Q&A, policy lookups, and engagement procedure guidance on demand" },
    ],
    kpis: {
      deliverable_prep_time_reduction: "-42%",
      billable_hours_recaptured: "26 hrs/wk",
      headcount_avoided: "2.0 FTE (~$140K/yr)",
      year_1_roi: "$108,000",
    },
    recommended_next_step: "Propose an Engagement Efficiency Pilot — deliverable drafting for tax season with 30-day ROI",
  },
  law_firm: {
    industry_label: "Law Firm",
    overall_score: 77,
    ai_maturity_score: 49,
    workflow_friction_score: 83,
    hours_recovered_per_week: 28,
    top_workflows: [
      { rank: 1, name: "Contract Review & Summarization", impact: "High",   estimated_annual_roi: "$74,000",
        description: "AI-assisted first-pass review of NDAs, MSAs, and standard agreements" },
      { rank: 2, name: "Matter Status Reporting",          impact: "High",   estimated_annual_roi: "$33,000",
        description: "Auto-compile attorney notes into client-ready progress summaries" },
      { rank: 3, name: "Intake & Conflict Screening",      impact: "Medium", estimated_annual_roi: "$21,000",
        description: "Classify new matters, surface conflicts, and draft engagement letters" },
    ],
    kpis: {
      contract_review_time_reduction: "-58%",
      associate_hours_recaptured: "28 hrs/wk",
      headcount_avoided: "1.5 FTE (~$112K/yr)",
      year_1_roi: "$128,000",
    },
    recommended_next_step: "Propose a Contract Intelligence Pilot — 30-day proof with measurable attorney time recapture",
  },
};
